/**
 * PersonAuth Aggregate Root
 * 
 * Enterprise-grade DDD implementation following Eric Evans principles:
 * - Single Aggregate Root (no separate entity/aggregate files)
 * - Rich domain model with encapsulated business logic
 * - Immutable state transitions
 * - Domain events for important state changes
 */
import { AggregateRootBase } from '../../../../shared/domain/AggregateRootBase';
import * as bcrypt from 'bcrypt';
import { v4 as uuidv4 } from 'uuid';

// Value Objects
export class Email {
  constructor(public readonly value: string) {
    if (!this.isValid(value)) {
      throw new Error(`Invalid email: ${value}`);
    }
  }

  private isValid(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  equals(other: Email): boolean {
    return this.value === other.value;
  }
}

export class HashedPassword {
  constructor(public readonly value: string) {
    if (!value || value.length < 20) {
      throw new Error('Invalid hashed password');
    }
  }

  async verify(plainPassword: string): Promise<boolean> {
    return bcrypt.compare(plainPassword, this.value);
  }

  static async create(plainPassword: string): Promise<HashedPassword> {
    if (plainPassword.length < 8) {
      throw new Error('Password must be at least 8 characters');
    }
    const hashed = await bcrypt.hash(plainPassword, 12);
    return new HashedPassword(hashed);
  }
}

// Domain Events
export interface PersonAuthCreated {
  aggregateId: string;
  personId: string;
  email: string;
  occurredAt: Date;
}

export interface LoginSucceeded {
  aggregateId: string;
  personId: string;
  email: string;
  sessionId: string;
  occurredAt: Date;
}

export interface LoginFailed {
  aggregateId: string;
  email: string;
  reason: string;
  attemptsRemaining: number;
  occurredAt: Date;
}

export interface PasswordChanged {
  aggregateId: string;
  personId: string;
  changedBy: 'user' | 'admin';
  occurredAt: Date;
}

export interface AccountLocked {
  aggregateId: string;
  personId: string;
  reason: string;
  lockedUntil: Date;
  occurredAt: Date;
}

// Authentication Result Value Objects
export class AuthenticationResult {
  private constructor(
    public readonly success: boolean,
    public readonly sessionId?: string,
    public readonly failureReason?: string,
    public readonly attemptsRemaining?: number
  ) {}

  static successful(sessionId: string): AuthenticationResult {
    return new AuthenticationResult(true, sessionId);
  }

  static failed(reason: string, attemptsRemaining: number): AuthenticationResult {
    return new AuthenticationResult(false, undefined, reason, attemptsRemaining);
  }

  static locked(): AuthenticationResult {
    return new AuthenticationResult(false, undefined, 'Account is locked', 0);
  }
}

/**
 * PersonAuth Aggregate Root
 * Manages authentication and authorization for a person
 */
export class PersonAuth extends AggregateRootBase {
  private static readonly MAX_LOGIN_ATTEMPTS = 5;
  private static readonly LOCKOUT_DURATION_MINUTES = 30;

  private constructor(
    id: string,
    private readonly personId: string,
    private readonly email: Email,
    private hashedPassword: HashedPassword,
    private isActive: boolean,
    private isEmailVerified: boolean,
    private failedLoginAttempts: number,
    private lastLoginAt?: Date,
    private emailVerifiedAt?: Date,
    private passwordChangedAt?: Date,
    private lockedUntil?: Date,
    version: number = 0
  ) {
    super(id, version);
  }

  /**
   * Factory method to create a new PersonAuth
   */
  static async create(
    personId: string,
    email: string,
    plainPassword: string
  ): Promise<PersonAuth> {
    const id = uuidv4();
    const emailVO = new Email(email);
    const hashedPassword = await HashedPassword.create(plainPassword);
    
    const personAuth = new PersonAuth(
      id,
      personId,
      emailVO,
      hashedPassword,
      true, // isActive
      false, // isEmailVerified - require verification
      0, // failedLoginAttempts
      undefined, // lastLoginAt
      undefined, // emailVerifiedAt
      new Date(), // passwordChangedAt
      undefined // lockedUntil
    );

    personAuth.addDomainEvent<PersonAuthCreated>({
      aggregateId: id,
      personId,
      email,
      occurredAt: new Date()
    });

    return personAuth;
  }

  /**
   * Reconstitute from persistence
   */
  static fromSnapshot(snapshot: any): PersonAuth {
    return new PersonAuth(
      snapshot.id,
      snapshot.personId,
      new Email(snapshot.email),
      new HashedPassword(snapshot.hashedPassword),
      snapshot.isActive,
      snapshot.isEmailVerified,
      snapshot.failedLoginAttempts,
      snapshot.lastLoginAt,
      snapshot.emailVerifiedAt,
      snapshot.passwordChangedAt,
      snapshot.lockedUntil,
      snapshot.version
    );
  }

  /**
   * Authenticate with password
   * This is the core business logic - not in a handler!
   */
  async authenticate(plainPassword: string): Promise<AuthenticationResult> {
    // Check if account is locked
    if (this.isLocked()) {
      this.addDomainEvent<LoginFailed>({
        aggregateId: this.id,
        email: this.email.value,
        reason: 'Account is locked',
        attemptsRemaining: 0,
        occurredAt: new Date()
      });
      return AuthenticationResult.locked();
    }

    // Check if account can login
    if (!this.canLogin()) {
      const reason = !this.isActive 
        ? 'Account is inactive' 
        : 'Email not verified';
      
      this.addDomainEvent<LoginFailed>({
        aggregateId: this.id,
        email: this.email.value,
        reason,
        attemptsRemaining: PersonAuth.MAX_LOGIN_ATTEMPTS - this.failedLoginAttempts,
        occurredAt: new Date()
      });
      
      return AuthenticationResult.failed(reason, PersonAuth.MAX_LOGIN_ATTEMPTS - this.failedLoginAttempts);
    }

    // Verify password
    const isValid = await this.hashedPassword.verify(plainPassword);
    
    if (!isValid) {
      this.recordFailedAttempt();
      const attemptsRemaining = Math.max(0, PersonAuth.MAX_LOGIN_ATTEMPTS - this.failedLoginAttempts);
      
      this.addDomainEvent<LoginFailed>({
        aggregateId: this.id,
        email: this.email.value,
        reason: 'Invalid password',
        attemptsRemaining,
        occurredAt: new Date()
      });

      if (this.failedLoginAttempts >= PersonAuth.MAX_LOGIN_ATTEMPTS) {
        this.lock();
      }

      return AuthenticationResult.failed('Invalid credentials', attemptsRemaining);
    }

    // Success
    const sessionId = uuidv4();
    this.recordSuccessfulLogin();
    
    this.addDomainEvent<LoginSucceeded>({
      aggregateId: this.id,
      personId: this.personId,
      email: this.email.value,
      sessionId,
      occurredAt: new Date()
    });

    return AuthenticationResult.successful(sessionId);
  }

  /**
   * Change password - domain logic, not in handler
   */
  async changePassword(
    currentPassword: string, 
    newPassword: string
  ): Promise<boolean> {
    // Verify current password
    const isCurrentValid = await this.hashedPassword.verify(currentPassword);
    if (!isCurrentValid) {
      return false;
    }

    // Set new password
    this.hashedPassword = await HashedPassword.create(newPassword);
    this.passwordChangedAt = new Date();
    this.failedLoginAttempts = 0; // Reset on password change
    this.lockedUntil = undefined; // Unlock on password change
    this.incrementVersion();

    this.addDomainEvent<PasswordChanged>({
      aggregateId: this.id,
      personId: this.personId,
      changedBy: 'user',
      occurredAt: new Date()
    });

    return true;
  }

  /**
   * Admin password reset
   */
  async resetPasswordByAdmin(newPassword: string): Promise<void> {
    this.hashedPassword = await HashedPassword.create(newPassword);
    this.passwordChangedAt = new Date();
    this.failedLoginAttempts = 0;
    this.lockedUntil = undefined;
    this.incrementVersion();

    this.addDomainEvent<PasswordChanged>({
      aggregateId: this.id,
      personId: this.personId,
      changedBy: 'admin',
      occurredAt: new Date()
    });
  }

  /**
   * Verify email
   */
  verifyEmail(): void {
    if (this.isEmailVerified) return;
    
    this.isEmailVerified = true;
    this.emailVerifiedAt = new Date();
    this.incrementVersion();
  }

  /**
   * Activate account
   */
  activate(): void {
    if (this.isActive) return;
    
    this.isActive = true;
    this.incrementVersion();
  }

  /**
   * Deactivate account
   */
  deactivate(): void {
    if (!this.isActive) return;
    
    this.isActive = false;
    this.incrementVersion();
  }

  // Private helper methods
  private recordFailedAttempt(): void {
    this.failedLoginAttempts++;
    this.incrementVersion();
  }

  private recordSuccessfulLogin(): void {
    this.failedLoginAttempts = 0;
    this.lastLoginAt = new Date();
    this.incrementVersion();
  }

  private lock(): void {
    const lockUntil = new Date();
    lockUntil.setMinutes(lockUntil.getMinutes() + PersonAuth.LOCKOUT_DURATION_MINUTES);
    this.lockedUntil = lockUntil;
    
    this.addDomainEvent<AccountLocked>({
      aggregateId: this.id,
      personId: this.personId,
      reason: 'Too many failed login attempts',
      lockedUntil: lockUntil,
      occurredAt: new Date()
    });
  }

  private isLocked(): boolean {
    if (!this.lockedUntil) return false;
    return this.lockedUntil > new Date();
  }

  private canLogin(): boolean {
    return this.isActive && this.isEmailVerified && !this.isLocked();
  }

  // Getters for read-only access
  getPersonId(): string { return this.personId; }
  getEmail(): string { return this.email.value; }
  isAccountActive(): boolean { return this.isActive; }
  isAccountVerified(): boolean { return this.isEmailVerified; }
  getFailedAttempts(): number { return this.failedLoginAttempts; }
  getLastLoginAt(): Date | undefined { return this.lastLoginAt; }

  // Snapshot for persistence
  toSnapshot(): any {
    return {
      id: this.id,
      personId: this.personId,
      email: this.email.value,
      hashedPassword: this.hashedPassword.value,
      isActive: this.isActive,
      isEmailVerified: this.isEmailVerified,
      failedLoginAttempts: this.failedLoginAttempts,
      lastLoginAt: this.lastLoginAt,
      emailVerifiedAt: this.emailVerifiedAt,
      passwordChangedAt: this.passwordChangedAt,
      lockedUntil: this.lockedUntil,
      version: this.version
    };
  }
}